import React, { useState } from 'react';
import styled from 'styled-components';

import { Modal, Spinner, Button, Text } from '@companycam/slab-web';

import { translate } from 'javascripts/translations/i18n';
import { useReviewProfiles } from '../hooks/useReviewProfiles';
import { useModal } from 'components/shared/hooks';
import toast from 'helpers/toast';
import ReviewProfilesDropdownList from 'components/reviews/ReviewProfilesDropdownList';
import ProjectSelect from './ProjectSelect';
import { type ReviewProfileObject } from 'data/v3/data-contracts';
import { type Project } from './types';
import { type NavigateFunction } from 'react-router';
import { useStoredSelectedReviewProfileId } from '../hooks/useStoredSelectedReviewProfileId';
import { useStoredSelectedFeedbackReviewProfileId } from '../hooks/useStoredSelectedFeedbackReviewProfileId';
// #region styled-components
const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  width: 100%;
`;
// #endregion styled-components

const ReviewRequestModal = ({
  navigate = () => {},
}: {
  navigate?: NavigateFunction;
}) => {
  const [project, setProject] = useState<Project | null>(null);
  const { reviewProfiles, isReviewProfilesLoading } = useReviewProfiles();
  const { storedSelectedReviewProfileId, setStoredSelectedReviewProfileId } =
    useStoredSelectedReviewProfileId();

  const { setStoredSelectedFeedbackReviewProfileId } =
    useStoredSelectedFeedbackReviewProfileId();

  const selectedPublicProfile = reviewProfiles.find(
    (reviewProfile) => reviewProfile.id === storedSelectedReviewProfileId,
  );

  const profileNotFound = !selectedPublicProfile && !isReviewProfilesLoading;
  const { hideModal } = useModal();

  const handleSelectedReviewProfile = (location: ReviewProfileObject) => {
    setStoredSelectedReviewProfileId(location.id);
  };

  const onRequestReviewClick = () => {
    setStoredSelectedFeedbackReviewProfileId(storedSelectedReviewProfileId);
    navigate(`/projects/${project?.node?.databaseId}/review-requests/new`);
    hideModal();
  };

  if (profileNotFound) {
    hideModal();
    toast.error(translate('common.uhOh'));
  }

  if (isReviewProfilesLoading) {
    return (
      <SpinnerContainer>
        <Spinner
          accessibilityLabel={translate('common.loading')}
          size="large"
        />
      </SpinnerContainer>
    );
  }

  return (
    <Modal
      title={null}
      headerComponent={null}
      size="small"
      isOpen
      onClose={hideModal}
      bodyStyle={{
        paddingBottom: 'var(--cc_size_spacing_xl)',
        paddingRight: 'var(--cc_size_spacing_xl)',
        paddingLeft: 'var(--cc_size_spacing_xl)',
        paddingTop: 'var(--cc_size_spacing_s)',
      }}
      footerActions={
        <>
          <Button onClick={hideModal}>{translate('common.cancel')}</Button>
          <Button
            color="primary"
            onClick={onRequestReviewClick}
            disabled={!project}
          >
            {translate('common.continue')}
          </Button>
        </>
      }
    >
      <Text
        as="span"
        size="s"
        weight={600}
        family="heading"
        ccMargin="l none s none"
      >
        {translate('project.reviews.selectLocation')}
      </Text>
      <Container>
        <ReviewProfilesDropdownList
          isInModal
          reviewProfiles={reviewProfiles}
          selectedReviewProfile={selectedPublicProfile}
          onReviewProfileSelected={handleSelectedReviewProfile}
        />
      </Container>
      <Text
        as="span"
        size="s"
        weight={600}
        family="heading"
        ccMargin="l none s none"
      >
        {translate('common.chooseAProject')}
      </Text>
      <ProjectSelect
        onProjectSelect={(p) => setProject(p)}
        selectedProject={project}
        placeholderText={translate(
          'project.reviews.reviewsHub.findReviewProject',
        )}
      />
    </Modal>
  );
};

export default ReviewRequestModal;
